import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Layers, Image as ImageIcon, Wand2 } from "lucide-react";
import IMG from "@/lib/images";

// Preserve `?ref=CODE` (and any other query string) when navigating
// from Hero → /signup, so a vendor referral URL propagates through
// the click. The cookie catches it too via ReferralCatcher in App.js;
// this is defense-in-depth for the (rare) case cookies are blocked.
function preserveQuery() {
  if (typeof window === "undefined") return "";
  return window.location.search || "";
}

export default function Hero() {
  // Sketch → render → multi-view "video loop". A 5-frame crossfade
  // through the pillar style images (Jadau → Kundan → Polki → Temple
  // → Meenakari), 1.2 s per frame, giving a natural 6 s loop that
  // mirrors what happens inside Facetra Studio. Motion library only,
  // no <video> tag — keeps first-contentful-paint fast and works
  // through Cloudflare's aggressive HTML-only prefetch. The staggered
  // multi-view thumbnails on the right pick up the same tick so the
  // whole hero feels like one live "watching AI generate" moment.
  const RENDER_FRAMES = [
    { src: IMG.jadau, style: "Jadau", time: "6.2s" },
    { src: IMG.kundan, style: "Kundan", time: "5.8s" },
    { src: IMG.polki || IMG.kundan, style: "Polki", time: "6.4s" },
    { src: IMG.temple, style: "Temple", time: "7.1s" },
    { src: IMG.meenakari, style: "Meenakari", time: "6.6s" },
  ];
  const FRAME_MS = 1200;
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setFrame((i) => (i + 1) % RENDER_FRAMES.length),
      FRAME_MS,
    );
    return () => clearInterval(t);
  }, []);
  const active = RENDER_FRAMES[frame];

  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
      {/* background glow */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 radial-gold-glow" />
        <div
          className="absolute inset-0 opacity-30 mix-blend-screen"
          style={{
            backgroundImage: `url(${IMG.bgGlow})`,
            backgroundPosition: "center top",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#0B0B0B]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="flex justify-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-white/70">
              AI Jewellery Design Studio
            </span>
          </div>
        </motion.div>

        {/* Headline — the H1 is our top on-page SEO signal. Primary
            keyword ("AI Studio for Jewellery Design") sits as the
            second line so the emotional hook lands first while the
            search-intent keyword still occupies the H1 element. */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: "easeOut" }}
          data-testid="hero-headline"
          className="mt-7 text-center font-display font-light tracking-[-0.03em] text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02]"
        >
          From Sketch to{" "}
          <span className="italic gold-gradient-text font-normal">Stunning Jewellery</span>
          <br className="hidden sm:block" /> AI Studio for Jewellery Design
        </motion.h1>

        {/* Subhead — keyword-dense but reads naturally. Hits Kundan
            (highest search volume), the four other pillar styles,
            plus the sketch → render → multi-view → CAD workflow
            language that matches our Phase 3 money pages. */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: "easeOut" }}
          className="mt-7 max-w-2xl mx-auto text-center text-base md:text-lg text-white/65 font-light leading-relaxed"
        >
          Facetra is the AI studio for jewellery design — turn sketches, references
          and CAD screenshots into photoreal renders, silhouette-locked multi-view
          sheets and production-ready 3D CAD in under a minute. Fluent in Kundan,
          Jadau, Polki, Diamond, Temple and Meenakari.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: "easeOut" }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3.5"
        >
          <Link
            to={`/signup${preserveQuery()}`}
            data-testid="hero-cta-start"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-7 py-3.5 hover:bg-[#F3CE5A] transition-all duration-300 shadow-[0_8px_36px_-8px_rgba(212,175,55,0.6)]"
          >
            <Sparkles className="w-4 h-4" strokeWidth={2} />
            Start Designing
            <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </Link>
          <a
            href="#showcase"
            data-testid="hero-cta-demo"
            className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.03] text-white text-sm px-6 py-3.5 hover:bg-white/[0.06] hover:border-white/30 transition-all duration-300"
          >
            <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
            Watch Demo
          </a>
        </motion.div>

        {/* Floating dashboard mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.55, ease: "easeOut" }}
          className="mt-20 md:mt-28 relative max-w-6xl mx-auto"
        >
          <div className="absolute -inset-x-10 -top-10 bottom-0 -z-10 rounded-[40px] bg-gradient-to-b from-[#D4AF37]/10 to-transparent blur-3xl" />

          <div className="relative glass-panel rounded-3xl p-3 md:p-4 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]">
            {/* window chrome */}
            <div className="flex items-center justify-between px-3 pt-1 pb-3">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                facetra · studio
              </div>
              <div className="text-[10px] text-white/30">v0.9 preview</div>
            </div>

            <div className="grid grid-cols-12 gap-3 md:gap-4">
              {/* Left: sketch — AI "reading" scan line implies live processing */}
              <div className="col-span-12 md:col-span-4 rounded-2xl border border-white/10 bg-[#111111] p-4 min-h-[280px] md:min-h-[420px] flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Input · Sketch</div>
                  <ImageIcon className="w-3.5 h-3.5 text-white/40" />
                </div>
                <div
                  className="relative flex-1 rounded-xl border border-dashed border-white/10 bg-[#0e0e0e] bg-cover bg-center overflow-hidden"
                  style={{ backgroundImage: `url(${IMG.heroSketch})` }}
                >
                  <motion.div
                    aria-hidden="true"
                    initial={{ y: "-30%" }}
                    animate={{ y: "130%" }}
                    transition={{
                      duration: 2.4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      repeatType: "reverse",
                    }}
                    className="absolute inset-x-0 h-8 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(to bottom, transparent 0%, rgba(212,175,55,0.35) 50%, transparent 100%)",
                    }}
                  />
                </div>
                <div className="mt-3 text-[11px] text-white/45">jadau_necklace_v2.png</div>
              </div>

              {/* Center: render — 6 s crossfade loop through pillar styles */}
              <div className="col-span-12 md:col-span-5 rounded-2xl border border-[#D4AF37]/30 bg-[#0E0E0E] p-4 min-h-[280px] md:min-h-[420px] flex flex-col relative overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                    AI Render · Photorealistic
                  </div>
                  <Wand2 className="w-3.5 h-3.5 text-[#D4AF37]" />
                </div>
                <div className="relative flex-1 rounded-xl overflow-hidden bg-[#0e0e0e]">
                  <AnimatePresence mode="sync" initial={false}>
                    <motion.div
                      key={active.src}
                      initial={{ opacity: 0, scale: 1.03 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${active.src})` }}
                    />
                  </AnimatePresence>
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px]">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={`s-${frame}`}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.35 }}
                      className="text-white/45"
                    >
                      Style · {active.style}
                    </motion.span>
                  </AnimatePresence>
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={`t-${frame}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35 }}
                      className="text-[#D4AF37]"
                    >
                      Generated in {active.time}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>

              {/* Right: views & controls */}
              <div className="col-span-12 md:col-span-3 flex flex-col gap-3">
                <div className="rounded-2xl border border-white/10 bg-[#111111] p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">
                    Multi-view
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[IMG.temple, IMG.meenakari, IMG.kundan, IMG.jadau].map((src, i) => {
                      // Highlight the tile whose style is currently
                      // playing in the centre panel — a quiet visual
                      // link between the render and its multi-view.
                      const styleName = ["Temple", "Meenakari", "Kundan", "Jadau"][i];
                      const isActive = active.style === styleName;
                      return (
                        <motion.div
                          key={i}
                          animate={{
                            borderColor: isActive
                              ? "rgba(212,175,55,0.75)"
                              : "rgba(255,255,255,0.1)",
                            boxShadow: isActive
                              ? "0 0 0 1px rgba(212,175,55,0.35), 0 6px 20px -6px rgba(212,175,55,0.35)"
                              : "0 0 0 0 rgba(0,0,0,0)",
                          }}
                          transition={{ duration: 0.5 }}
                          className="aspect-square rounded-lg bg-cover bg-center border"
                          style={{ backgroundImage: `url(${src})` }}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#111111] p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">
                    Presets
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {["Jadau", "Polki", "Meenakari", "Temple"].map((s) => {
                      const isActive = active.style === s;
                      return (
                        <motion.span
                          key={s}
                          animate={{
                            backgroundColor: isActive
                              ? "rgba(212,175,55,0.15)"
                              : "rgba(0,0,0,0)",
                            borderColor: isActive
                              ? "rgba(212,175,55,0.5)"
                              : "rgba(255,255,255,0.1)",
                            color: isActive
                              ? "rgb(212,175,55)"
                              : "rgba(255,255,255,0.6)",
                          }}
                          transition={{ duration: 0.5 }}
                          className="text-[10px] px-2.5 py-1 rounded-full border"
                        >
                          {s}
                        </motion.span>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#111111] p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">
                    Output
                  </div>
                  <div className="space-y-1.5 text-[11px] text-white/55">
                    <div className="flex justify-between"><span>4K Render</span><span className="text-white/35">PNG</span></div>
                    <div className="flex justify-between"><span>Tech Sheet</span><span className="text-white/35">PDF</span></div>
                    <div className="flex justify-between"><span>CAD ref</span><span className="text-white/35">STL</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating badges */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="hidden lg:flex absolute -left-10 top-32 glass-panel rounded-2xl px-4 py-3 items-center gap-3"
          >
            <Layers className="w-4 h-4 text-[#D4AF37]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Variations</div>
              <div className="text-sm text-white">12 generated</div>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="hidden lg:flex absolute -right-6 bottom-20 glass-panel rounded-2xl px-4 py-3 items-center gap-3"
          >
            <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Weight estimate</div>
              <div className="text-sm text-white">18.6g · 22kt</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Trust strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-16 md:mt-20 text-center"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/35 mb-6">
            Crafted for ateliers across India
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-white/40 font-display tracking-wide">
            {["Jaipur", "Mumbai", "Surat", "Chennai", "Hyderabad", "Kolkata"].map((c) => (
              <span key={c} className="text-sm md:text-base italic">{c}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
